// src/components/ReceiptPrint.jsx
import React, { useContext } from "react";
import styled from "styled-components";
import AppContext from "../contexts/AppContext";
import moment from "moment";

const ReceiptContainer = styled.div`
  width: 100%;
  max-width: 380px; /* narrow receipt width, good for screenshot */
  background: #ffffff;
  padding: 12px;
  border-radius: 6px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.06);
  font-family: ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto,
    "Helvetica Neue", Arial;
`;

/* helper utils */
const filterPropsByName = (namesToRemove, sourceArray) =>
  (sourceArray || []).filter((c) => !namesToRemove.includes(c.name));

const getObjectByName = (name, sourceArray) => {
  let item = (sourceArray || []).find((o) => o.name === name);
  return item || {};
};

const safeArray = (v) => (Array.isArray(v) ? v : []);

const parseAmountNumber = (s) => {
  if (!s && s !== 0) return 0;
  try {
    const n = String(s).replace(/[^0-9\-\.]/g, "");
    return Number(n) || 0;
  } catch {
    return 0;
  }
};

const formatN = (n) =>
  typeof n === "number" ? new Intl.NumberFormat("en-NG").format(n) : n;

export default function ReceiptPrint({ data, containerId = "receipt-print" }) {
  const { context } = useContext(AppContext);

  if (!data) return null;

  const {
    amount = [],
    buyer_seller = [],
    company = [],
    currency = [],
    status = [],
    document = [],
    remark = {},
  } = data;

  const lines = data?.line_data ?? [];
  let header = (data?.line_header ?? [])[0]?.name ?? [];
  header = header?.filter((m) => !/download|attachment/i.test(m));
  const attachments =
    (data?.attachment ?? []).map(({ name }) => name)?.flat() ?? [];

  const docRef =
    getObjectByName("Ref #", document)?.value ||
    getObjectByName("Ref", document)?.value ||
    "";
  const docDate =
    getObjectByName("Date", document)?.value ||
    moment().format("DD/MM/YYYY, hh:mm:ss A");

  const numericAmounts = safeArray(amount)
    .map((a) => parseAmountNumber(a?.value))
    .filter((v) => !isNaN(v));
  const computedSubtotal =
    numericAmounts.length > 0
      ? numericAmounts.reduce((s, v) => s + v, 0)
      : null;

  const getLineDisplay = (row) => {
    const qty = row?.QUANTITY ?? row?.INPUT_QUANTITY ?? "";
    const desc = row?.ITEM ?? row?.DESCRIPTION ?? row?.ITEM_DESCRIPTION ?? "";
    const price = row?.PRICE ?? row?.UNIT_AMOUNT ?? row?.CURRENT_PRICE ?? "";
    const total = row?.TOTAL_AMOUNT ?? row?.TOTAL ?? row?.TOTAL_AMOUNT ?? "";
    return { qty, desc, price, total };
  };

  // Render
  return (
    <div className="flex justify-center py-6">
      <ReceiptContainer id={containerId}>
        {/* Header */}
        <div className="text-center">
          {[company, buyer_seller].map((entity, idx) => {
            const logo =
              idx === 1
                ? context?.app?.logo?.[0] ||
                  getObjectByName("Logo", entity)?.value
                : getObjectByName("Logo", entity)?.value;
            const others = filterPropsByName(
              ["Logo", "Email", "Phone Number"],
              entity
            );
            const unique = [...new Set(others.map((item) => item.value))];

            return (
              <div key={idx} className="py-2 break-words whitespace-pre-wrap">
                {logo && (
                  <div className="mb-2 flex justify-center">
                    <img
                      src={logo}
                      alt="logo"
                      style={{ maxHeight: 40, objectFit: "contain" }}
                    />
                  </div>
                )}

                <div className="my-3 border-t border-dotted border-gray-300" />

                {unique.map((value, i) =>
                  value ? (
                    <div
                      key={i}
                      className="font-black text-lg tracking-wider break-words whitespace-pre-wrap"
                    >
                      {value}
                    </div>
                  ) : null
                )}
              </div>
            );
          })}

          <div className="text-xs mt-1 text-gray-600 break-words whitespace-pre-wrap">
            {getObjectByName("Address", company)?.value ?? ""}
          </div>
        </div>

        {/* separator */}
        <div className="my-3 border-t border-dotted border-gray-300" />

        {/* meta */}
        <div className="text-xs mb-2">
          <div className="flex justify-between">
            <div className="tracking-wider break-words whitespace-pre-wrap">
              Ref: {docRef}
            </div>
            <div className="tracking-wider break-words whitespace-pre-wrap">
              {docDate}
            </div>
          </div>
        </div>

        <div className="border-t border-dotted border-gray-300 my-2" />

        {/* items header */}
        <div className="grid grid-cols-12 text-[12px] font-mono">
          <div className="col-span-2 font-semibold">Qty</div>
          <div className="col-span-6 font-semibold">Description</div>
          <div className="col-span-2 text-right font-semibold">Price</div>
          <div className="col-span-2 text-right font-semibold">Amount</div>
        </div>

        {/* lines */}
        <div className="mt-2">
          {lines.length === 0 ? (
            <div className="text-center text-xs text-gray-500 py-6">
              No items
            </div>
          ) : (
            lines.map((row, idx) => {
              const { qty, desc, price, total } = getLineDisplay(row);
              return (
                <div
                  key={idx}
                  className="grid grid-cols-12 text-[12px] items-start py-1"
                >
                  <div className="col-span-2 tracking-wider break-words whitespace-pre-wrap">
                    {qty}
                  </div>
                  <div className="col-span-6 tracking-wider break-words whitespace-pre-wrap">
                    {desc}
                  </div>
                  <div
                    className="col-span-2 text-right tracking-wider break-words whitespace-pre-wrap"
                    dangerouslySetInnerHTML={{ __html: price }}
                  />
                  <div
                    className="col-span-2 text-right tracking-wider break-words whitespace-pre-wrap"
                    dangerouslySetInnerHTML={{ __html: total || "" }}
                  />
                </div>
              );
            })
          )}
        </div>

        <div className="border-t border-dotted border-gray-300 my-2" />

        {/* totals */}
        <div className="flex justify-end">
          <div className="w-full">
            {safeArray(amount).map((item, idx, arr) => {
              const isBeforeLast = idx === arr.length - 1;

              return (
                <React.Fragment key={idx}>
                  {isBeforeLast && (
                    <div className="border-t border-dotted border-gray-300 my-2" />
                  )}

                  <div className="flex flex-1 justify-between py-1">
                    <p className="text-gray-700 text-[12px]">{item?.name}</p>
                    <div
                      className="font-semibold break-words whitespace-pre-wrap text-[12px]"
                      dangerouslySetInnerHTML={{ __html: item?.value }}
                    />
                  </div>
                </React.Fragment>
              );
            })}

            {safeArray(amount).length === 0 && computedSubtotal !== null && (
              <div className="flex justify-between py-1 text-[13px]">
                <div className="text-gray-700">Subtotal</div>
                <div className="font-semibold tracking-wider break-words whitespace-pre-wrap">
                  N{formatN(computedSubtotal)}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="border-t border-dotted border-gray-300 my-2" />

        <div className="text-xs text-center mt-2">
          <div className="font-semibold tracking-wider break-words whitespace-pre-wrap">
            THANK YOU!
          </div>
          <div className="text-gray-600 mt-2 tracking-wider break-words whitespace-pre-wrap">
            {remark?.value ?? ""}
          </div>
        </div>

        {/* <div className="mt-4 flex flex-col items-center">
          {attachments.length > 0 && (
            <div className="mb-2 text-xs">Attachments</div>
          )}
          {docRef && (
            <div
              style={{
                width: "100%",
                height: 48,
                background:
                  "repeating-linear-gradient(90deg,#000 0 2px, transparent 2px 6px)",
              }}
            />
          )}
        </div> */}
      </ReceiptContainer>
    </div>
  );
}
